import type { Incident, IncidentGroup } from '../../services/incidentsApi';
import { isGroupedIncident } from './incidentPatternSemantics';

export type IncidentReviewSurfaceMode =
  | 'pattern_case'
  | 'observation_disposition'
  | 'standalone_incident';

export type ResolveIncidentReviewSurfaceModeArgs = {
  entryMode?: 'pattern_case' | 'observation_disposition' | null;
  reviewExactMode?: boolean;
  selectedIncident?: Incident | null;
  selectedGroupContext?: IncidentGroup | null;
  openedWithoutGroupContext?: boolean;
};

export function resolveIncidentReviewSurfaceMode(
  args: ResolveIncidentReviewSurfaceModeArgs,
): IncidentReviewSurfaceMode {
  const incident = args.selectedIncident ?? null;
  const groupContext = args.selectedGroupContext ?? null;

  if (!isGroupedIncident(incident as any, groupContext)) {
    return 'standalone_incident';
  }

  if (args.entryMode) {
    return args.entryMode;
  }

  if (args.reviewExactMode) {
    return 'observation_disposition';
  }

  const hasGroupKey = Boolean(String(groupContext?.group_key || '').trim());
  if (!hasGroupKey || args.openedWithoutGroupContext) {
    return 'observation_disposition';
  }

  return 'pattern_case';
}

export function isPatternCaseReviewMode(mode: IncidentReviewSurfaceMode): boolean {
  return mode === 'pattern_case';
}

export function isObservationDispositionMode(mode: IncidentReviewSurfaceMode): boolean {
  return mode === 'observation_disposition';
}

export function isStandaloneIncidentReviewMode(mode: IncidentReviewSurfaceMode): boolean {
  return mode === 'standalone_incident';
}

/**
 * Better-proof / representative hops must not yank pattern-case review into observation
 * disposition. Exact pinning stays for true observation deep links and non-workflow opens.
 */
export function shouldPinExactIncidentForProofHop(args: {
  currentlyExact?: boolean;
  workflowId?: string | null;
}): boolean {
  if (args.currentlyExact) return true;
  if (String(args.workflowId || '').trim()) return false;
  return true;
}

export function openFullProofReviewHop(args: {
  incidentId?: string | null;
  open: (id: string, opts: { exact: boolean }) => void;
  currentlyExact: boolean;
  workflowId?: string | null;
}): void {
  const id = String(args.incidentId || '').trim();
  if (!id) return;
  args.open(id, {
    exact: shouldPinExactIncidentForProofHop({
      currentlyExact: args.currentlyExact,
      workflowId: args.workflowId,
    }),
  });
}
